import {
  applyStep,
  createGameWithEvents,
  DEFAULT_GAME_SIZE,
  sizedSetup,
  undoDepth as engineUndoDepth,
  type Action,
  type GameEvent,
  type GameSetup,
  type GameSizeId,
  type GameState,
  type GameStep,
  type Reason,
  type Ruleset,
  type Side,
} from '@brig/rules-engine';
import { getNation } from '@brig/nations';

export type Nations = Readonly<Record<Side, string>>;

/** Default nations of a new sandbox game, until the players pick their own. */
export const DEFAULT_NATIONS: Nations = { red: 'spain', blue: 'france' };

/** Each side's player colour (an id from PLAYER_COLOURS), chosen apart from the nation. */
export type Colours = Readonly<Record<Side, string>>;

export const DEFAULT_COLOURS: Colours = { red: 'red', blue: 'blue' };

/** Hot-seat players: both sides sit at the same screen, named after their nations. */
export function sandboxPlayers(nations: Nations) {
  return {
    red: { id: 'sandbox-red', name: getNation(nations.red).names.en },
    blue: { id: 'sandbox-blue', name: getNation(nations.blue).names.en },
  };
}

/**
 * The whole sandbox game: setup, the action log and every intermediate state, so undo is O(1).
 * `states[0]` is the initial position, `states[i + 1]` follows `actions[i]`.
 */
export interface History {
  readonly setup: GameSetup;
  /** Presentation only: the engine knows sides, not nations or colours. */
  readonly nations: Nations;
  readonly colours: Colours;
  readonly actions: readonly Action[];
  readonly states: readonly GameState[];
  readonly events: readonly (readonly GameEvent[])[];
  readonly error: HistoryError | null;
}

/** Why the last attempt failed: a rule violation (with its reason) or a broken file/setup. */
export interface HistoryError {
  readonly rule: string | null;
  readonly reason: Reason | null;
  readonly message: string;
}

const plainError = (message: string): HistoryError => ({ rule: null, reason: null, message });

export type HistoryMessage =
  | { readonly type: 'apply'; readonly action: Action }
  /** Steps the server accepted, with the chance it dealt (R-RNG-03). */
  | { readonly type: 'commit'; readonly steps: readonly GameStep[] }
  /** Several actions as one intent: all of them, or none if any is illegal. */
  | { readonly type: 'applyAll'; readonly actions: readonly Action[] }
  /** Back over deterministic actions only (docs/ux.md U-UNDO-01). */
  | { readonly type: 'undo' }
  /** Dev tool: back one action, whatever it was (U-UNDO-05). */
  | { readonly type: 'rewind' }
  | {
      readonly type: 'reset';
      readonly setup: GameSetup;
      readonly nations: Nations;
      readonly colours: Colours;
      readonly actions?: readonly Action[];
    }
  | { readonly type: 'recolour'; readonly colours: Colours }
  | { readonly type: 'fail'; readonly message: string }
  | { readonly type: 'dismissError' };

/** A new game of the given size (map, fleet and forts all follow from it). */
export function newSetup(
  seed: number,
  ruleset: Partial<Ruleset> = {},
  nations: Nations = DEFAULT_NATIONS,
  size: GameSizeId = DEFAULT_GAME_SIZE,
): GameSetup {
  return sizedSetup(size, seed, sandboxPlayers(nations), ruleset);
}

/**
 * Builds a history by replaying `actions`. Throws on an invalid setup or an illegal action,
 * naming the offending action index.
 */
export function buildHistory(
  setup: GameSetup,
  actions: readonly Action[] = [],
  nations: Nations = DEFAULT_NATIONS,
  colours: Colours = DEFAULT_COLOURS,
): History {
  return historyOf(
    setup,
    actions.map((action) => ({ action })),
    nations,
    colours,
  );
}

/** Builds a history by replaying a game's steps (R-RNG-03), as the server sends them. */
export function historyOf(
  setup: GameSetup,
  steps: readonly GameStep[],
  nations: Nations = DEFAULT_NATIONS,
  colours: Colours = DEFAULT_COLOURS,
): History {
  const created = createGameWithEvents(setup.map, setup.ruleset, setup.seed, setup.players, setup.options);
  let history: History = {
    setup,
    nations,
    colours,
    actions: [],
    states: [created.state],
    events: [created.events],
    error: null,
  };
  steps.forEach((s, i) => {
    const next = step(history, s);
    if (next.error) throw new Error(`Action #${i + 1} (${s.action.type}): ${formatError(next.error)}`);
    history = next;
  });
  return history;
}

export function current(history: History): GameState {
  return history.states[history.states.length - 1] as GameState;
}

function step(history: History, s: GameStep): History {
  const result = applyStep(current(history), s);
  if (!result.ok) return { ...history, error: result.error };
  return {
    ...history,
    actions: [...history.actions, s.action],
    states: [...history.states, result.state],
    events: [...history.events, result.events],
    error: null,
  };
}

export function historyReducer(history: History, message: HistoryMessage): History {
  switch (message.type) {
    case 'apply':
      return step(history, { action: message.action });
    case 'applyAll':
      return stepAll(
        history,
        message.actions.map((action) => ({ action })),
      );
    case 'commit':
      return stepAll(history, message.steps);
    case 'undo':
      return undoDepth(history) === 0 ? history : dropLast(history);
    case 'rewind':
      return history.actions.length === 0 ? history : dropLast(history);
    case 'reset':
      try {
        return buildHistory(message.setup, message.actions, message.nations, message.colours);
      } catch (e) {
        return { ...history, error: plainError(e instanceof Error ? e.message : String(e)) };
      }
    case 'recolour':
      return { ...history, colours: message.colours };
    case 'fail':
      return { ...history, error: plainError(message.message) };
    case 'dismissError':
      return { ...history, error: null };
  }
}

/** All the steps, or none if any fails. */
function stepAll(history: History, steps: readonly GameStep[]): History {
  let next = history;
  for (const s of steps) {
    next = step(next, s);
    if (next.error) return { ...history, error: next.error };
  }
  return next;
}

function dropLast(history: History): History {
  return {
    ...history,
    actions: history.actions.slice(0, -1),
    states: history.states.slice(0, -1),
    events: history.events.slice(0, -1),
    error: null,
  };
}

/** How many actions undo may take back: the deterministic ones since the last barrier (U-UNDO-01). */
export function undoDepth(history: History): number {
  return engineUndoDepth(history.actions);
}

export function formatError(error: HistoryError): string {
  return error.rule ? `${error.rule}: ${error.message}` : error.message;
}

/** A random 31-bit seed for "new game". UI-only randomness; the engine stays deterministic. */
export function randomSeed(): number {
  return Math.floor(Math.random() * 0x7fffffff);
}
